// 요금제 한도 판정(동시 진행 현장 · 사진·첨부 용량 · 알림톡 월 건수) — 막지 않고 경고 수준만 정한다(서명·접수·착공·올리기·발송은 그대로 된다).

import { formatDateTime } from "../../lib/format";
import type { CustomerNotifyMonthCount } from "../customer-notify/types";
import { ACTIVE_SITE_STATUSES, DEFAULT_PLAN_CODE, type OrgPlan, type PlanName, type PlanView } from "./types";
import type { ProjectStatus } from "../project/types";

export type SiteLimitLevel = "ok" | "warn" | "over";

export interface SiteLimitInput {
  activeSites: number;
  limit: number | null;
  graceUntil: string | null;
  now: Date;
}

// 행이 없으면 초대(무제한) · 초대는 한도 열을 무시한다.
export const planViewOf = (row: Pick<OrgPlan, "plan" | "active_site_limit" | "grace_until"> | null): PlanView =>
  row
    ? { plan: row.plan, limit: row.plan === "invited" ? null : row.active_site_limit, graceUntil: row.grace_until }
    : { plan: DEFAULT_PLAN_CODE, limit: null, graceUntil: null };

const inGrace = (graceUntil: string | null, now: Date): boolean => {
  const until = graceUntil ? Date.parse(graceUntil) : Number.NaN;
  return Number.isFinite(until) && now.getTime() < until;
};

// 한도 미만 ok · 도달 warn · 초과는 유예 중 warn, 유예가 없거나 끝났으면 over.
export const evaluateSiteLimit = ({ activeSites, limit, graceUntil, now }: SiteLimitInput): SiteLimitLevel => {
  if (limit === null || activeSites < limit) return "ok";
  if (activeSites === limit) return "warn";
  return inGrace(graceUntil, now) ? "warn" : "over";
};

// 경고 한 줄 — ok 면 null.
export const siteLimitMessage = (input: SiteLimitInput): string | null => {
  const level = evaluateSiteLimit(input);
  if (level === "ok" || input.limit === null) return null;
  if (input.activeSites === input.limit) return `진행 중 현장이 한도(${input.limit}곳)에 닿았습니다.`;
  const head = `진행 중 현장 ${input.activeSites}곳으로 한도(${input.limit}곳)를 넘었습니다.`;
  return level === "warn"
    ? `${head} ${formatDateTime(input.graceUntil)}까지 유예 중입니다.`
    : `${head} 요금제를 확인해 주세요.`;
};

// 한도 근접 기준(%) — 한도의 이 비율 이상이거나 도달·초과면 근접(정수로 비교해 소수 오차가 없다).
export const SITE_NEAR_LIMIT_PERCENT = 80;

export interface SiteUsage {
  activeSites: number;
  limit: number | null;
  // 한도 대비 비율 — 한도 없음·0 이면 null.
  rate: number | null;
  level: SiteLimitLevel;
  near: boolean;
}

// 사용률과 한도 근접 판정 한 벌 — 운영자 목록·상세가 같이 쓴다.
export const siteUsageOf = (input: SiteLimitInput): SiteUsage => {
  const level = evaluateSiteLimit(input);
  const limit = input.limit;
  return {
    activeSites: input.activeSites,
    limit,
    rate: limit !== null && limit > 0 ? input.activeSites / limit : null,
    level,
    near: limit !== null && (level !== "ok" || input.activeSites * 100 >= limit * SITE_NEAR_LIMIT_PERCENT),
  };
};

// 근접 경고 한 줄 — 도달·초과는 siteLimitMessage, 그 아래 근접은 비율 문구, 아니면 null.
export const siteNearMessage = (input: SiteLimitInput): string | null => {
  const message = siteLimitMessage(input);
  if (message) return message;
  const usage = siteUsageOf(input);
  return usage.near && usage.limit !== null
    ? `진행 중 현장 ${usage.activeSites}곳으로 한도(${usage.limit}곳)의 ${SITE_NEAR_LIMIT_PERCENT}% 이상입니다.`
    : null;
};

// 시공중 현장 수 — 인메모리 저장소와 현장 목록이 같은 규칙을 쓴다(Supabase 는 같은 상수로 head count).
export const countActiveSites = (projects: readonly { status: ProjectStatus }[]): number => {
  const active: readonly ProjectStatus[] = ACTIVE_SITE_STATUSES;
  return projects.filter((p) => active.includes(p.status)).length;
};

// ── 용량·알림톡 — 한도의 80% 이상 near · 100% 이상 over · 막지 않는다 ──
export type UsageLevel = "ok" | "near" | "over";

export const USAGE_NEAR_PERCENT = 80;

export interface UsageLimits {
  storageBytes: number | null;
  notifyMonth: number | null;
}

export interface UsageMeter {
  used: number;
  limit: number | null;
  // 한도 대비 비율 — 한도 없음·0 이면 null.
  rate: number | null;
  level: UsageLevel;
}

export interface PlanUsage {
  storage: UsageMeter;
  notify: UsageMeter;
  // 경고 문구 — 용량·알림톡 순 · ok 는 빠진다.
  warnings: string[];
}

// 행이 없거나 초대면 제한 없음 · 열이 없거나 null 이어도 제한 없음.
export const usageLimitsOf = (
  row: Pick<OrgPlan, "plan" | "storage_limit_bytes" | "notify_month_limit"> | null,
): UsageLimits =>
  !row || row.plan === "invited"
    ? { storageBytes: null, notifyMonth: null }
    : { storageBytes: row.storage_limit_bytes ?? null, notifyMonth: row.notify_month_limit ?? null };

// 정수 곱으로 비교해 소수 오차가 없다 · 한도 0 은 하나라도 쓰면 over.
export const usageLevelOf = (used: number, limit: number | null): UsageLevel => {
  if (limit === null) return "ok";
  if (limit <= 0) return used > 0 ? "over" : "ok";
  if (used >= limit) return "over";
  return used * 100 >= limit * USAGE_NEAR_PERCENT ? "near" : "ok";
};

export const usageMeterOf = (used: number, limit: number | null): UsageMeter => ({
  used,
  limit,
  rate: limit !== null && limit > 0 ? used / limit : null,
  level: usageLevelOf(used, limit),
});

const GB = 1024 ** 3;
const MB = 1024 ** 2;

// 바이트 한 줄 — 1GB 이상은 GB 소수 한 자리 · 그 아래는 MB 정수 · 0 초과 1MB 미만은 「1MB 미만」(0MB 로 보이지 않게).
export const formatBytes = (bytes: number): string => {
  if (bytes >= GB) return `${(Math.round((bytes / GB) * 10) / 10).toString()}GB`;
  if (bytes > 0 && bytes < MB) return "1MB 미만";
  return `${Math.round(bytes / MB)}MB`;
};

// near 띠엔 「출시 알림 받기」 링크가 없어(over 만) 링크를 안내하지 않는다.
const PREPARING_NOTE = "유료 요금제는 준비 중입니다.";
const RELEASE_NOTE = "유료 요금제는 준비 중입니다 — 「출시 알림 받기」로 먼저 안내받으세요.";

// 용량 경고 한 줄 — ok 면 null.
export const storageUsageMessage = (meter: UsageMeter): string | null => {
  if (meter.level === "ok" || meter.limit === null) return null;
  const amount = `${formatBytes(meter.used)} / ${formatBytes(meter.limit)}`;
  return meter.level === "near"
    ? `사진·첨부 저장 공간을 ${USAGE_NEAR_PERCENT}% 넘게 썼습니다(${amount}). ${PREPARING_NOTE}`
    : `사진·첨부 저장 공간 한도를 다 썼습니다(${amount}). 올리기는 계속 됩니다. ${RELEASE_NOTE}`;
};

// 알림톡 경고 한 줄 — ok 면 null.
export const notifyUsageMessage = (meter: UsageMeter): string | null => {
  if (meter.level === "ok" || meter.limit === null) return null;
  const amount = `${meter.used}/${meter.limit}건`;
  return meter.level === "near"
    ? `이번 달 알림톡을 ${USAGE_NEAR_PERCENT}% 넘게 보냈습니다(${amount}). ${PREPARING_NOTE}`
    : `이번 달 알림톡 한도를 다 썼습니다(${amount}). 발송은 계속 됩니다. ${RELEASE_NOTE}`;
};

// 용량·알림톡 한 벌 — 알림톡은 실제 발송(sent)만 센다(기록만 logOnly 는 빼고).
export const planUsageOf = (input: {
  row: OrgPlan | null;
  storageBytes: number;
  notify: CustomerNotifyMonthCount;
}): PlanUsage => {
  const limits = usageLimitsOf(input.row);
  const storage = usageMeterOf(input.storageBytes, limits.storageBytes);
  const notify = usageMeterOf(input.notify.sent, limits.notifyMonth);
  const warnings = [storageUsageMessage(storage), notifyUsageMessage(notify)].filter((m): m is string => m !== null);
  return { storage, notify, warnings };
};

export interface PlanPanel {
  plan: PlanName;
  activeSites: number;
  limit: number | null;
  level: SiteLimitLevel;
  warning: string | null;
  notify: CustomerNotifyMonthCount;
  usage: PlanUsage;
}

// 설정 「요금제」 한 벌 — 저장소 값을 받아 판정까지 끝낸다.
export const planPanelOf = (input: {
  row: OrgPlan | null;
  activeSites: number;
  notify: CustomerNotifyMonthCount;
  storageBytes: number;
  now: Date;
}): PlanPanel => {
  const view = planViewOf(input.row);
  const limitInput = { activeSites: input.activeSites, limit: view.limit, graceUntil: view.graceUntil, now: input.now };
  return {
    plan: view.plan,
    activeSites: input.activeSites,
    limit: view.limit,
    level: evaluateSiteLimit(limitInput),
    warning: siteLimitMessage(limitInput),
    notify: input.notify,
    usage: planUsageOf({ row: input.row, storageBytes: input.storageBytes, notify: input.notify }),
  };
};

// 현장 목록 경고 한 줄 — 대표만 · 필터 전 조직 전체의 시공중 현장 수(project.countActive)로 판정 · 막지 않는다.
export const ownerSiteWarning = (input: {
  isOwner: boolean;
  activeSites: number;
  row: OrgPlan | null;
  now: Date;
}): string | null => {
  if (!input.isOwner) return null;
  const view = planViewOf(input.row);
  return siteLimitMessage({
    activeSites: input.activeSites,
    limit: view.limit,
    graceUntil: view.graceUntil,
    now: input.now,
  });
};
